/**
 * Helpers para o BancoSelect (Bloco BSEL.2).
 *
 * Lista estatica de ~270 bancos brasileiros (snapshot da BrasilAPI curado).
 * Sem latencia, sem dep externa, sem manutencao automatica — atualizar
 * snapshot manualmente quando aparecer demanda (raro).
 */

import BANCOS from "./bancosBR.json";

// Top 12 bancos mais usados pela cliente-piloto + populares no Brasil.
// Aparecem na secao "Mais usados" no topo do combobox quando search vazio.
export const BANCOS_FAVORITOS_CODIGOS = [
  "001", // Banco do Brasil
  "104", // Caixa Economica Federal
  "237", // Bradesco
  "341", // Itau Unibanco
  "033", // Santander
  "260", // Nubank
  "077", // Inter
  "208", // BTG Pactual
  "756", // Sicoob
  "748", // Sicredi
  "336", // C6 Bank
  "212", // Original
];

// Lazy: indice por codigo para lookup O(1).
let _indicePorCodigo = null;
function indicePorCodigo() {
  if (!_indicePorCodigo) {
    _indicePorCodigo = new Map();
    BANCOS.forEach((b) => _indicePorCodigo.set(b.codigo, b));
  }
  return _indicePorCodigo;
}

/**
 * Retorna o objeto banco completo por codigo COMPE (3 digitos).
 * @param {string} codigo Codigo COMPE (ex: "237", "341").
 * @returns {{codigo, nome, nomeCurto, ispb} | null}
 */
export function obterBanco(codigo) {
  if (!codigo) return null;
  const c = String(codigo).padStart(3, "0");
  return indicePorCodigo().get(c) || null;
}

/**
 * Busca bancos por nome OU codigo. Caso-insensitivo, sem acentos.
 * @param {string} query Termo de busca.
 * @param {number} limite Maximo de resultados (default 50).
 * @returns {Array<{codigo, nome, nomeCurto, ispb}>}
 */
export function buscarBancos(query, limite = 50) {
  if (!query) return BANCOS.slice(0, limite);
  const q = normalizar(query);
  const resultados = [];
  for (const banco of BANCOS) {
    if (resultados.length >= limite) break;
    if (
      banco.codigo.startsWith(q) ||
      normalizar(banco.nomeCurto).includes(q) ||
      normalizar(banco.nome).includes(q)
    ) {
      resultados.push(banco);
    }
  }
  return resultados;
}

/**
 * Retorna lista dos bancos favoritos (BANCOS_FAVORITOS_CODIGOS na ordem).
 * Filtra os que existem no snapshot — defensivo.
 */
export function bancosFavoritos() {
  return BANCOS_FAVORITOS_CODIGOS
    .map((c) => obterBanco(c))
    .filter(Boolean);
}

/**
 * Retorna lista completa ordenada (favoritos no topo, depois alfabetica).
 * Usada quando search vazio: secao "Mais usados" + secao "Todos".
 */
export function bancosOrdenados() {
  const favs = new Set(BANCOS_FAVORITOS_CODIGOS);
  const naoFavoritos = BANCOS.filter((b) => !favs.has(b.codigo))
    .sort((a, b) => a.nomeCurto.localeCompare(b.nomeCurto, "pt-BR"));
  return {
    favoritos: bancosFavoritos(),
    todos: naoFavoritos,
  };
}

/** Total de bancos no snapshot (exposto pra mostrar "Todos (N)" na UI). */
export function totalBancos() {
  return BANCOS.length;
}

// Helpers privados
function normalizar(texto) {
  return String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, ""); // remove acentos
}
